import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, User, LogOut, LayoutDashboard, Plus, Search, CreditCard, Podcast, Info } from 'lucide-react';

export const Navbar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="navbar-glass" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-10 h-10 bg-emerald-deep rounded-full flex items-center justify-center">
              <span className="text-white font-display font-bold text-lg">U</span>
            </div>
            <span className="font-display text-xl font-semibold text-emerald-deep hidden sm:block">
              Uprize Black Biz
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/directory"
              className="text-stone-600 hover:text-emerald-deep font-medium transition-colors flex items-center gap-2"
              data-testid="nav-directory"
            >
              <Search className="w-4 h-4" />
              Explore
            </Link>
            <Link
              to="/podcasts"
              className="text-stone-600 hover:text-emerald-deep font-medium transition-colors flex items-center gap-2"
              data-testid="nav-podcasts"
            >
              <Podcast className="w-4 h-4" />
              Podcasts
            </Link>
            <Link
              to="/pricing"
              className="text-stone-600 hover:text-emerald-deep font-medium transition-colors flex items-center gap-2"
              data-testid="nav-pricing"
            >
              <CreditCard className="w-4 h-4" />
              Pricing
            </Link>
            <Link
              to="/about"
              className="text-stone-600 hover:text-emerald-deep font-medium transition-colors flex items-center gap-2"
              data-testid="nav-about"
            >
              <Info className="w-4 h-4" />
              About
            </Link>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 hover:bg-emerald-deep/5"
                    data-testid="nav-user-menu"
                  >
                    {user?.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-emerald-deep/10 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-emerald-deep" />
                      </div>
                    )}
                    <span className="font-medium text-stone-700">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="nav-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/add-business')} data-testid="nav-add-business">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Business
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/pricing')} data-testid="nav-upgrade">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Upgrade Plan
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" className="text-stone-600 hover:text-emerald-deep" data-testid="nav-login">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full px-6" data-testid="nav-register">
                    Get Started
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-stone-600"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="nav-mobile-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-stone-100" data-testid="nav-mobile-menu">
            <div className="flex flex-col gap-2">
              <Link
                to="/directory"
                className="px-4 py-3 text-stone-600 hover:bg-stone-50 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Explore Directory
              </Link>
              <Link
                to="/podcasts"
                className="px-4 py-3 text-stone-600 hover:bg-stone-50 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Podcasts
              </Link>
              <Link
                to="/pricing"
                className="px-4 py-3 text-stone-600 hover:bg-stone-50 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </Link>
              <Link
                to="/about"
                className="px-4 py-3 text-stone-600 hover:bg-stone-50 rounded-lg font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                About Us
              </Link>
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    className="px-4 py-3 text-stone-600 hover:bg-stone-50 rounded-lg font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/add-business"
                    className="px-4 py-3 text-stone-600 hover:bg-stone-50 rounded-lg font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Add Business
                  </Link>
                  <Link
                    to="/settings"
                    className="px-4 py-3 text-stone-600 hover:bg-stone-50 rounded-lg font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Settings
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="px-4 py-3 text-left text-red-600 hover:bg-red-50 rounded-lg font-medium"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="px-4 py-3 text-stone-600 hover:bg-stone-50 rounded-lg font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-3 bg-emerald-deep text-white rounded-lg font-medium text-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
